import { useState, useEffect } from 'react';
import SchemaOrg from '../../components/base/SchemaOrg';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    area: '',
    message: ''
  });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const pageTitle = '聯絡永業環保｜預約專業清潔服務｜0800-281-281';
    const pageDesc = '聯絡永業環保，預約水塔清洗、水管疏通、水溝清理等專業服務。免費服務專線：0800-281-281，全年無休24小時服務，服務桃園、新北、台北、新竹、苗栗地區。';
    const pageKeywords = '聯絡永業環保,預約清潔服務,水塔清洗電話,免費諮詢,0800-281-281';
    const pageUrl = `${siteUrl}/contact`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);
  }, [siteUrl]);

  const faqItems = [
    {
      q: '你們的服務範圍包含哪些地區？',
      a: '我們主要服務桃園市、新北市、台北市、新竹縣市、苗栗縣市等地區、歡迎來電洽詢,我們會盡力為您安排服務。'
    },
    {
      q: '需要提前多久預約？',
      a: '歡迎立即來電預約,我們會配合您的時間。如有緊急需求,也可立即到府解決您的問題。'
    },
    {
      q: '費用如何計算？',
      a: '費用會依據服務項目、現場狀況、施工難度等因素評估。我們提供出車費3000元到府估價服務,確認報價後才會進行施工,施工完會扣除出車費,絕不額外加價。'
    },
    {
      q: '施工需要多長時間？',
      a: '依服務項目不同,一般水塔清洗約2-3小時,管道疏通約1-2小時。實際時間會依現場狀況調整,我們會在施工前告知預估時間。'
    }
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#organization`,
      name: '永業環保',
      telephone: '0800-281-281',
      email: 'service@yongyeh.com',
      url: siteUrl,
      address: {
        '@type': 'PostalAddress',
        addressRegion: '桃園市',
        addressCountry: 'TW'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${siteUrl}/contact#contactpage`,
      url: `${siteUrl}/contact`,
      name: '聯絡我們 - 永業環保',
      description: '聯絡永業環保，預約專業清潔服務。24小時服務專線：0800-281-281',
      mainEntity: {
        '@id': `${siteUrl}/#organization`
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/contact#faqpage`,
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/contact#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '聯絡我們', item: `${siteUrl}/contact` }
      ]
    }
  ];

  return (
    <>
      <SchemaOrg schema={schemas} />
      {/* Hero 區域 */}
      <section className="relative pt-32 pb-20 md:pt-32 md:pb-20 py-16 bg-gradient-to-br from-[#f0f9ff] to-[#e0f2fe]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-12 mb-8">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">聯絡我們</h1>
            <p className="text-lg md:text-xl text-gray-600">專業團隊隨時為您服務</p>
          </div>
        </div>
      </section>

      {/* 快速聯絡方式 */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-3xl mx-auto mb-12 md:mb-20">
            <a href="tel:0800-281-281" className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 text-center cursor-pointer">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl md:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">免費服務專線</h3>
              <p className="text-xl md:text-2xl font-bold text-[#1e3a5f] mb-2">0800-281-281</p>
              <p className="text-sm text-gray-600">全年無休</p>
            </a>

            <a href="tel:0906629777" className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 text-center cursor-pointer">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-smartphone-line text-2xl md:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">行動電話</h3>
              <p className="text-xl md:text-2xl font-bold text-[#1e3a5f] mb-2">0906-629-777</p>
              <p className="text-sm text-gray-600">全年無休</p>
            </a>

            <a href="https://line.me/ti/p/wJ4U8JR3pC" target="_blank" rel="noopener noreferrer" className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 text-center cursor-pointer">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-[#06C755] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-line-fill text-2xl md:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">LINE 諮詢</h3>
              <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-3">
                <img
                  src="https://static.readdy.ai/image/c61c43d626627dd077148c0c867066d5/7052ea82e329e82c5f01b86dda25596f.jpeg"
                  alt="LINE QR Code"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-sm text-gray-600">掃描加入 LINE 即時諮詢</p>
            </a>
          </div>

          {/* 公司資訊 */}
          <div className="max-w-4xl mx-auto">
            <div className="mb-8 text-center">
              <span className="inline-block bg-white border border-gray-300 text-gray-800 px-4 py-2 rounded-full text-sm font-medium mb-4">COMPANY INFO</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">公司資訊</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10">
              <div className="flex items-start gap-4 bg-[#f8fafc] rounded-xl p-5 md:p-6">
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">公司地址</h3>
                  <p className="text-gray-600 text-sm md:text-base">桃園市中壢區仁美二街113號1樓</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-[#f8fafc] rounded-xl p-5 md:p-6">
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">營業時間</h3>
                  <p className="text-gray-600 text-sm md:text-base">24H 全年無休</p>
                  <p className="text-sm text-gray-500 mt-1">國定假日照常營業</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-[#f8fafc] rounded-xl p-5 md:p-6">
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">服務範圍</h3>
                  <p className="text-gray-600 text-sm md:text-base">桃園市、新北市、台北市、新竹縣市、苗栗縣市</p>
                  <p className="text-sm text-gray-500 mt-1"></p>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d327000!2d121.05!3d24.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1szh-TW!2stw!4v1700000000000!5m2!1szh-TW!2stw"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="永業環保服務區域地圖 - 桃竹苗"
                className="md:h-[400px]"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ 區域 */}
      <section className="py-12 md:py-20 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <span className="inline-block bg-white text-gray-800 px-6 py-2 rounded-full text-sm font-medium mb-4">FAQ</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">常見問題</h2>
          </div>

          <div className="space-y-3 md:space-y-4">
            {faqItems.map((faq, index) => (
              <details key={index} className="bg-white rounded-xl p-4 md:p-6 shadow-md hover:shadow-lg transition-all cursor-pointer">
                <summary className="font-bold text-gray-900 text-base md:text-lg flex items-center justify-between cursor-pointer">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-xl flex-shrink-0 ml-2"></i>
                </summary>
                <p className="text-gray-600 text-sm md:text-base mt-3 md:mt-4 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}